import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { debounceTime, filter, map, Observable, of, switchMap, tap } from 'rxjs';
import { MedicalComponentService } from 'shared/domain';

import { UsableComponentFilter, ExtendedComponentFilter, ComponentSearchResultDto } from '@treetop/shared/model';

import { FilterOptions, SearchOptions } from './model/filter-options';

export type MedicalComponentSearchComponentState = {
  loading: boolean;
  searchResults?: ComponentSearchResultDto;
  searchOptions?: SearchOptions;
  error: HttpErrorResponse | null;
};

@Injectable()
export class MedicalComponentSearchComponentService extends ComponentStore<MedicalComponentSearchComponentState> {
  private medicalComponentHttpService = inject(MedicalComponentService);

  public vm$ = this.select((state) => state);

  public searchMedicalComponents = this.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => this.patchState({ loading: true, searchResults: {} })),
      debounceTime(300),
      switchMap(() => this.search()),
      tapResponse<ComponentSearchResultDto>(
        (response) => this.patchState({ searchResults: response, loading: false, error: null }),
        (error: HttpErrorResponse) => {
          console.error(error);
          this.patchState({ error, loading: false });
        },
      ),
    ),
  );

  constructor() {
    super({
      loading: false,
      searchResults: null,
      error: null,
    });

    this.select((state) => state.searchOptions)
      .pipe(filter((options) => options?.searchText?.length >= 3))
      .subscribe(() => {
        this.searchMedicalComponents();
      });
  }

  private search(): Observable<ComponentSearchResultDto> {
    const { searchText, filter } = this.get().searchOptions ?? { searchText: '', filter: null };

    if (!filter || !searchText || searchText.length < 3 || filter.possibleComponentTypes.length === 0) {
      return of({});
    }

    const baseFilter = {
      searchText,
      componentTypes: filter.possibleComponentTypes,
      taxonomyId: filter.taxonomyId,
    };

    if (filter.usableComponents) {
      const usableComponentFilter: UsableComponentFilter = {
        ...baseFilter,
        includeProposedComponents: filter.useProposedComponent,
      };

      return this.medicalComponentHttpService.loadUsableDefinitionsByType(usableComponentFilter);
    }

    const extendedComponentFilter: ExtendedComponentFilter = {
      ...baseFilter,
      componentSubTypes: filter.possibleComponentSubTypes,
      componentStates: filter.componentStates,
    };

    return this.medicalComponentHttpService.loadDefinitions(extendedComponentFilter).pipe(
      map((result) => {
        // Create a new instance of ComponentSearchResultDto
        const componentSearchResult: ComponentSearchResultDto = {
          // Assuming ComponentSearchResultDto has properties like `list` and `totalCount`
          components: result.list ?? [], // Map the list from the result
        };

        return componentSearchResult;
      }),
    );
  }

  public updateFilter(options: { searchText?: string; filter?: FilterOptions }): void {
    const currentSearchOptions = this.get().searchOptions;
    this.patchState({
      searchOptions: {
        searchText: options.searchText ?? currentSearchOptions?.searchText,
        filter: options.filter ?? currentSearchOptions?.filter,
      },
    });
  }
}
